import React, { useCallback, useEffect, useState } from 'react';
import { Fade } from 'react-awesome-reveal';
import config from 'react-global-configuration';
import { useTranslation } from 'react-i18next';
import Immutable from 'immutable';
import Cookies from 'js-cookie';
import { WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';
import { DEFAULT_COOKIE_EXPIRATION_DAYS } from '@uncinc/uncinc-react-kitchen-sink/dist/components/GoogleConsentCookieBanner';

import { Button, CoreLink } from '../../../components';

import './style.scss';


const FeedbackToolBlock = ({ entity, parentEntityType, parentEntityId }) => {
  if (!entity || !parentEntityType || !parentEntityId) {
    return null;
  }

  const ANSWERS = {
    NEGATIVE: -1,
    POSITIVE: 1,
  };

  const setCookie = (answer) => {
    Cookies.set(`feedback-given-${parentEntityId}`, answer, { expires: DEFAULT_COOKIE_EXPIRATION_DAYS });
  };

  const getCookie = () => {
    const cookieValue = Cookies.get(`feedback-given-${parentEntityId}`);
    const numberValue = Number(cookieValue);
    if (!Number.isNaN(numberValue)) {
      return numberValue;
    }
    return null;
  };

  const { t } = useTranslation();

  const API_URL = config.get('API_URL');
  const title = entity.getIn(['field_title', 0, 'value'], t('feedback.title'));
  const positiveFeedbackLink = entity.getIn(['field_positive_feedback_link', 0, 'uri']);
  const negativeFeedbackLink = entity.getIn(['field_negative_feedback_link', 0, 'uri']);
  const positiveFeedbackLinkText = entity.getIn(['field_positive_feedback_link', 0, 'title']);
  const negativeFeedbackLinkText = entity.getIn(['field_negative_feedback_link', 0, 'title']);
  const positiveFeedbackDescription = entity.getIn(['field_positive_feedback_descript', 0, 'value']);
  const negativeFeedbackDescription = entity.getIn(['field_negative_feedback_descript', 0, 'value']);

  const [givenAnswer, setGivenAnswer] = useState();
  const [voted, setVoted] = useState(givenAnswer);

  useEffect(() => {
    const cookie = getCookie();
    setGivenAnswer(cookie);
    setVoted(cookie !== null);
  }, [parentEntityId]);

  const postVote = useCallback((value) => {
    if (voted) {
      return;
    }

    setVoted(true);

    const PATH = API_URL;
    fetch(`${PATH}/rest/session/token?_format=json`, {
      credentials: 'include',
    })
      .then((response) => response.text())
      .then((response) => {
        const csrfToken = response;
        fetch(`${PATH}/entity/vote/${parentEntityId}?_format=json`, {
          method: 'POST',
          credentials: 'include',
          body: JSON.stringify({
            'type': [
              {
                'target_id': 'vote',
                'target_type': 'vote_type',
              },
            ],
            'entity_type': [
              {
                'value': parentEntityType,
              },
            ],
            'entity_id': [
              {
                'target_id': parentEntityId,
              },
            ],
            'value': [
              {
                'value': value,
              },
            ],
            'value_type': [
              {
                'value': 'points',
              },
            ],
          }),
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
          },
        })
          .then((response) => response.json())
          .then((json) => {
            return Immutable.fromJS(json);
          })
          .then(() => {
            setGivenAnswer(value);
            setCookie(value);
          })
          .catch((error) => {
            setVoted(false);
            console.error(error);
          });
      })
      .catch((error) => {
        setVoted(false);
        console.error(error);
      });
  }, [setVoted, setGivenAnswer, voted]);

  const onPositiveFeedbackClicked = () => {
    postVote(ANSWERS.POSITIVE);
  };

  const onNegativeFeedbackClicked = () => {
    postVote(ANSWERS.NEGATIVE);
  };

  return (
    <Fade triggerOnce bottom distance="50px" duration={500}>
      <div className="FeedbackTool">
        <h4 className="FeedbackTool__title">{title}</h4>
        <div className="FeedbackTool__answers">
          {(givenAnswer === null || (givenAnswer && givenAnswer !== ANSWERS.NEGATIVE)) && (
            <Button className="icon-variant-4" withIcon icon="thumbsUp" button disabled={givenAnswer && voted} onClick={onPositiveFeedbackClicked}>{t('feedback.positive')}</Button>
          )}
          {(givenAnswer === null || (givenAnswer && givenAnswer !== ANSWERS.POSITIVE)) && (
            <Button className="icon-variant-4" withIcon icon="thumbsDown" button disabled={givenAnswer && voted} onClick={onNegativeFeedbackClicked}>{t('feedback.negative')}</Button>
          )}
        </div>
        {givenAnswer === ANSWERS.NEGATIVE && (
          <>
            {negativeFeedbackDescription && (
              <WysiwygField
                property="schema:text"
                html={negativeFeedbackDescription}
              />
            )}
            {negativeFeedbackLink && negativeFeedbackLinkText && (
              <CoreLink className="Button Button--primary Button--icon Link icon-after icon-arrow-right icon-before moving-arrow icon-variant-4" to={negativeFeedbackLink}>{negativeFeedbackLinkText}</CoreLink>
            )}
          </>
        )}

        {givenAnswer === ANSWERS.POSITIVE && (
          <>
            {positiveFeedbackDescription && (
              <WysiwygField
                property="schema:text"
                html={positiveFeedbackDescription}
              />
            )}
            {positiveFeedbackLink && positiveFeedbackLinkText && (
              <CoreLink className="Button Button--primary Button--icon Link icon-after icon-arrow-right icon-before moving-arrow icon-variant-4" to={positiveFeedbackLink}>{positiveFeedbackLinkText}</CoreLink>
            )}
          </>
        )}
      </div>
    </Fade>
  );
};

export default FeedbackToolBlock;
